'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

// 12-hour and 24-hour clock conversion. Midnight is 0000 and noon is 1200, the
// two cases people actually get wrong, so both are spelled out in the output.

function parse24(value: string): { hours: number; minutes: number } | null {
  const digits = value.replace(/[^0-9]/g, '');
  if (digits.length !== 3 && digits.length !== 4) return null;
  const padded = digits.padStart(4, '0');
  const hours = Number(padded.slice(0, 2));
  const minutes = Number(padded.slice(2));
  if (hours > 23 || minutes > 59) return null;
  return { hours, minutes };
}

function to12(hours: number, minutes: number): string {
  const suffix = hours < 12 ? 'AM' : 'PM';
  const display = hours % 12 === 0 ? 12 : hours % 12;
  return `${display}:${String(minutes).padStart(2, '0')} ${suffix}`;
}

function to24(hours12: number, minutes: number, meridiem: 'AM' | 'PM'): string {
  let hours = hours12 % 12;
  if (meridiem === 'PM') hours += 12;
  return `${String(hours).padStart(2, '0')}${String(minutes).padStart(2, '0')}`;
}

function spoken(hours: number, minutes: number): string {
  if (hours === 0 && minutes === 0) return 'zero hundred hours, midnight';
  if (hours === 12 && minutes === 0) return 'twelve hundred hours, noon';
  if (minutes === 0) return `${String(hours).padStart(2, '0')} hundred hours`;
  return `${String(hours).padStart(2, '0')} ${String(minutes).padStart(2, '0')} hours`;
}

export default function BrowserMilitaryTime() {
  const usageReported = useRef(false);
  const [militaryInput, setMilitaryInput] = useState('1330');
  const [hours12, setHours12] = useState(1);
  const [minutes12, setMinutes12] = useState(30);
  const [meridiem, setMeridiem] = useState<'AM' | 'PM'>('PM');

  const parsed = useMemo(() => parse24(militaryInput), [militaryInput]);

  useEffect(() => {
    if (!usageReported.current && parsed) {
      usageReported.current = true;
      void consumeToolUsage('military-time-converter', '').catch(() => undefined);
    }
  }, [parsed]);

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>Military time converter</h2>
          <p>
            Convert 24-hour military time to the 12-hour clock and back. Both directions update as
            you type, entirely in your browser.
          </p>
        </div>
        <span className="tool-live-badge">24h</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader" style={{ alignItems: 'stretch', gap: '1rem' }}>
          <label>
            Military time (for example 1330 or 0045)
            <input
              inputMode="numeric"
              onChange={(event) => setMilitaryInput(event.target.value)}
              placeholder="1330"
              type="text"
              value={militaryInput}
            />
          </label>

          {parsed ? (
            <div className="tool-live-stats">
              <div>
                <strong>{to12(parsed.hours, parsed.minutes)}</strong>
                <span>standard 12-hour time</span>
              </div>
              <div>
                <strong>{spoken(parsed.hours, parsed.minutes)}</strong>
                <span>how it is said out loud</span>
              </div>
            </div>
          ) : (
            <p className="tool-live-panel-note">
              Enter three or four digits, from 0000 to 2359. A colon is fine, it is ignored.
            </p>
          )}
        </div>

        <aside className="tool-live-panel">
          <strong>The other direction</strong>
          <label>
            Hour
            <select onChange={(event) => setHours12(Number(event.target.value))} value={hours12}>
              {Array.from({ length: 12 }, (_, index) => index + 1).map((hour) => (
                <option key={hour} value={hour}>
                  {hour}
                </option>
              ))}
            </select>
          </label>
          <label>
            Minute
            <select
              onChange={(event) => setMinutes12(Number(event.target.value))}
              value={minutes12}
            >
              {Array.from({ length: 60 }, (_, index) => index).map((minute) => (
                <option key={minute} value={minute}>
                  {String(minute).padStart(2, '0')}
                </option>
              ))}
            </select>
          </label>
          <label>
            AM or PM
            <select
              onChange={(event) => setMeridiem(event.target.value as 'AM' | 'PM')}
              value={meridiem}
            >
              <option value="AM">AM</option>
              <option value="PM">PM</option>
            </select>
          </label>

          <div className="tool-live-stats">
            <div>
              <strong>{to24(hours12, minutes12, meridiem)}</strong>
              <span>military time</span>
            </div>
          </div>

          <p className="tool-live-panel-note">
            Midnight is 0000 and noon is 1200. The two are a common mix-up because 12 AM and 12 PM
            read alike, so the converter always shows the word for those cases.
          </p>
        </aside>
      </div>
    </article>
  );
}
